import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { ClipboardList, Pill, FileText, Calendar, Image, Printer, Download, ArrowLeft } from "lucide-react";
import PrescriptionTemplate, { generatePrescriptionPDF } from "../utils/PrescriptionTemplate";
import { useAuth } from "../context/AuthContext";
import { buildApiUrl } from '../services/apiHelpers';
// Inline formatIST (formerly from istTime.js)
const formatIST = (date, options = {}) => {
  const d = new Date(date);
  const defaultOptions = { timeZone: 'Asia/Kolkata', ...options };
  return d.toLocaleString('en-IN', defaultOptions);
};

// Headers come from server cookie or AuthContext; no localStorage fallbacks.
const getClinicHeaders = (auth) => {
  if (auth?.clinic?.id) return { "X-Clinic-Id": auth.clinic.id };
  return {};
};

const PrescriptionView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [prescription, setPrescription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [patient, setPatient] = useState(null);
  const [doctor, setDoctor] = useState(null);
  const [appointment, setAppointment] = useState(null);
  const [clinic, setClinic] = useState(null);

  const { auth } = useAuth();
  const headersFromAuth = auth?.clinic?.id ? { "X-Clinic-Id": auth.clinic.id } : {};

  useEffect(() => {
    fetchPrescription();
  }, [id, auth]);

  const fetchPrescription = async () => {
    try {
      setLoading(true);
      
      const headers = Object.keys(headersFromAuth).length ? headersFromAuth : getClinicHeaders();
      const clinicId = headers["X-Clinic-Id"];
      if (clinicId) {
        console.log("[DEBUG] Using Clinic ID:", clinicId);
      } else {
        console.warn("[WARN] No clinic ID found in storage, attempting without header");
      }

      const response = await axios.get(buildApiUrl(`prescriptions/${id}`), { headers });

      // Backend may return either a Prescription object or an enriched object { prescription, doctor }
      let rx = response.data;
      if (!rx) {
        setError("Prescription not found");
        toast.error("Prescription not found");
        return;
      }

      if (rx.prescription) {
        // enriched shape
        setPrescription(rx.prescription);
        if (rx.doctor) setDoctor(rx.doctor);
        if (rx.clinic) setClinic(rx.clinic);
        // fetch patient/appointment if not provided
        if (rx.prescription.patientId) fetchPatient(rx.prescription.patientId);
        if (rx.prescription.appointmentId) fetchAppointment(rx.prescription.appointmentId);
        if (!rx.doctor && rx.prescription.doctorId) fetchDoctor(rx.prescription.doctorId, rx.prescription.clinicId);
        if (!rx.clinic && rx.prescription.clinicId) fetchClinic(rx.prescription.clinicId);
      } else {
        // plain prescription object
        setPrescription(rx);
        if (rx.patientId) fetchPatient(rx.patientId);
        if (rx.doctorId) fetchDoctor(rx.doctorId, rx.clinicId);
        if (rx.appointmentId) fetchAppointment(rx.appointmentId);
        if (rx.clinicId) fetchClinic(rx.clinicId);
      }
    } catch (err) {
      console.error("Error fetching prescription:", err);
      const errorMsg = err.response?.data?.message || 
                       err.response?.data?.error || 
                       (err.response?.status === 403 ? "Unauthorized access to this prescription" :
                        err.response?.status === 404 ? "Prescription not found" :
                        "Failed to load prescription");
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const fetchPatient = async (patientId) => {
    try {
      const headers = Object.keys(headersFromAuth).length ? headersFromAuth : getClinicHeaders();
      const response = await axios.get(buildApiUrl(`patients/${patientId}`), { headers });
      setPatient(response.data);
    } catch (err) {
      console.error("Error fetching patient:", err);
    }
  };

  // Fetch doctor by clinic context + id (backend exposes by-clinic list)
  const fetchDoctor = async (doctorId, clinicId) => {
    if (!doctorId) return;
    try {
      const headers = Object.keys(headersFromAuth).length ? headersFromAuth : getClinicHeaders();
      // prefer endpoint that lists doctors for a clinic and pick the matching id
      if (clinicId) {
        const res = await axios.get(buildApiUrl(`doctors/by-clinic-id/${clinicId}`), { headers });
        if (Array.isArray(res.data)) {
          const found = res.data.find(d => Number(d.id) === Number(doctorId) || Number(d.doctorId) === Number(doctorId));
          if (found) { setDoctor(found); return; }
        }
      }

      // fallback: attempt direct endpoint if available
      const fallback = await axios.get(buildApiUrl(`doctors/${doctorId}`), { headers }).catch(() => null);
      if (fallback && fallback.data) setDoctor(fallback.data);
    } catch (err) {
      console.error("Error fetching doctor:", err);
    }
  };

  const fetchAppointment = async (appointmentId) => {
    try {
      const headers = Object.keys(headersFromAuth).length ? headersFromAuth : getClinicHeaders();
      const response = await axios.get(buildApiUrl(`appointments/${appointmentId}`), { headers });
      setAppointment(response.data);
    } catch (err) {
      console.error("Error fetching appointment:", err);
    }
  };

  const fetchClinic = async (clinicId) => {
    if (!clinicId) return;
    try {
      const headers = Object.keys(headersFromAuth).length ? headersFromAuth : getClinicHeaders();
      const response = await axios.get(buildApiUrl(`settings/clinic-profile/${clinicId}`), { headers });
      setClinic(response.data);
    } catch (err) {
      console.error("Error fetching clinic:", err);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = async () => {
    try {
      // Generate PDF from backend-fetched prescription
      const fileOrBlob = await generatePrescriptionPDF(prescription.id);
      const blob = fileOrBlob instanceof Blob ? fileOrBlob : new Blob([fileOrBlob], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      // Open in new tab so user can save
      window.open(url, '_blank');
      // revoke after some time
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      console.error("Error downloading PDF:", err);
      toast.error("Failed to download PDF");
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingText}>Loading prescription...</div>
      </div>
    );
  }

  if (error || !prescription) {
    return (
      <div style={styles.container}>
        <div style={styles.errorText}>{error || "Prescription not found"}</div>
        <button style={styles.backButton} onClick={() => navigate(-1)}>
          <ArrowLeft size={16} style={{ marginRight: 8 }} /> Go Back
        </button>
      </div>
    );
  }

  return (
    <div style={styles.pageContainer}>
      {/* Print Controls */}
      <div style={styles.printControls}>
        <button style={styles.printButton} onClick={handlePrint}>
          <Printer size={16} style={{ marginRight: 8 }} /> Print
        </button>
        <button style={styles.downloadButton} onClick={handleDownloadPDF}>
          <Download size={16} style={{ marginRight: 8 }} /> Download as PDF
        </button>
        <button style={styles.backButton} onClick={() => navigate(-1)}>
          <ArrowLeft size={16} style={{ marginRight: 8 }} /> Go Back
        </button>
      </div>

      {/* Prescription Content */}
      <div style={styles.prescriptionContainer}>
        {/* Header */}
        <div style={styles.header}>
          <h1 style={styles.rxSymbol}>Rx</h1>
          <h2 style={styles.title}>Electronic Prescription</h2>
          <p style={styles.date}>{formatIST(prescription.createdAt)}</p>
        </div>

        {/* Patient & Doctor Info */}
        <div style={styles.infoGrid}>
          <div style={styles.infoSection}>
            <h3 style={styles.sectionTitle}>Patient Information</h3>
            {patient ? (
              <>
                <p><strong>Name:</strong> {patient.name || "N/A"}</p>
                <p><strong>Phone:</strong> {patient.phone || "N/A"}</p>
                <p><strong>Age:</strong> {patient.age || "N/A"}</p>
              </>
            ) : (
              <p>ID: {prescription.patientId}</p>
            )}
          </div>

          <div style={styles.infoSection}>
            <h3 style={styles.sectionTitle}>Doctor Information</h3>
            {doctor ? (
              <>
                <p><strong>Name:</strong> Dr. {doctor.doctorName || doctor.doctor_name || doctor.name || "N/A"}</p>
                <p><strong>Specialty:</strong> {doctor.specialization || "N/A"}</p>
                <p><strong>Registration:</strong> {doctor.registrationNumber || doctor.registration_number || doctor.registration || "N/A"}</p>
                <p><strong>Phone:</strong> {doctor.doctorPhone || doctor.doctor_phone || doctor.phoneNumber || doctor.phone || "Not available"}</p>
                <p><strong>Email:</strong> {doctor.email || doctor.doctorEmail || doctor.doctor_email || "Not available"}</p>
              </>
            ) : (
              <p>ID: {prescription.doctorId}</p>
            )}
          </div>
        </div>
        <div style={styles.infoGrid}>
          <div style={styles.infoSection}>
            <h3 style={styles.sectionTitle}>Clinic Information</h3>
            {clinic ? (
              <>
                <p><strong>Name:</strong> {clinic.clinic_name || clinic.clinicName || "N/A"}</p>
                <p><strong>Phone:</strong> {clinic.contact_number || clinic.phoneNumber || clinic.contactNumber || "Not available"}</p>
                <p><strong>Email:</strong> {clinic.email || "Not available"}</p>
                <p><strong>Address:</strong> {clinic.address_line || clinic.addressLine || clinic.address || "Not available"}{clinic.city ? `, ${clinic.city}` : ''}{clinic.state_name || clinic.stateName ? `, ${clinic.state_name || clinic.stateName}` : ''}</p>
                {(doctor && (doctor.defaultPrescriptionSchedule || doctor.default_prescription_schedule || doctor.defaultPrescriptionTimings || doctor.default_prescription_timings)) ? (
                  <p><strong>Clinic Timings:</strong> {doctor.defaultPrescriptionSchedule || doctor.default_prescription_schedule || doctor.defaultPrescriptionTimings || doctor.default_prescription_timings}</p>
                ) : null}
              </>
            ) : (
              <p>ID: {prescription.clinicId}</p>
            )}
          </div>
        </div>

        {/* Diagnosis */}
        {prescription.diagnosis && (
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}><ClipboardList size={16} style={{ marginRight: 8, verticalAlign: 'middle' }} />Chief Complaint / Diagnosis</h3>
            <p style={styles.sectionContent}>{prescription.diagnosis}</p>
          </div>
        )}

        {/* Medicines */}
        {prescription.medicines && (
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}><Pill size={16} style={{ marginRight: 8, verticalAlign: 'middle' }} />Prescribed Medicines</h3>
            <div style={styles.medicinesList}>
              {prescription.medicines.split('\n').filter(m => m.trim()).map((medicine, idx) => (
                <div key={idx} style={styles.medicineItem}>
                  {medicine.trim()}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Notes & Instructions */}
        {prescription.notes && (
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}><FileText size={16} style={{ marginRight: 8, verticalAlign: 'middle' }} />Clinical Notes & Instructions</h3>
            <p style={styles.sectionContent}>{prescription.notes}</p>
          </div>
        )}

        {/* Follow-up */}
        {prescription.nextFollowUpDate && (
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}><Calendar size={16} style={{ marginRight: 8, verticalAlign: 'middle' }} />Next Follow-up</h3>
            <p style={styles.sectionContent}>{prescription.nextFollowUpDate}</p>
          </div>
        )}

        {/* Upload Image */}
        {prescription.imageUrl && (
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}><Image size={16} style={{ marginRight: 8, verticalAlign: 'middle' }} />Prescription Image</h3>
            <img 
              src={prescription.imageUrl} 
              alt="Prescription" 
              style={styles.prescriptionImage}
            />
          </div>
        )}

        {/* Status & Fee Info */}
        <div style={styles.statusSection}>
          <div style={styles.statusItem}>
            <strong>Status:</strong>
            <span style={{...styles.statusBadge, ...getStatusStyle(prescription.status)}}>
              {prescription.status}
            </span>
          </div>
          {prescription.consultationFeeAssigned && (
            <div style={styles.statusItem}>
              <strong>Consultation Fee:</strong> ₹{prescription.consultationFeeAssigned}
            </div>
          )}
          {prescription.consultationFeePaid && (
            <div style={styles.statusItem}>
              <strong>Amount Paid:</strong> ₹{prescription.consultationFeePaid}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          <p>This is an electronically generated prescription from CliniQueue</p>
          <p>Prescription ID: {prescription.id}</p>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body { margin: 0; padding: 0; }
          div { page-break-inside: avoid; }
          .print-controls { display: none !important; }
        }
      `}</style>
    </div>
  );
};

const styles = {
  pageContainer: {
    minHeight: "100vh",
    backgroundColor: "#f5f7fa",
    padding: "20px",
    fontFamily: "'Lato', sans-serif",
  },
  printControls: {
    display: "flex",
    gap: "12px",
    marginBottom: "20px",
    justifyContent: "center",
    flexWrap: "wrap",
  },
  printButton: {
    padding: "10px 20px",
    backgroundColor: "#4CAF50",
    color: "white",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontWeight: "500",
    fontSize: "14px",
  },
  downloadButton: {
    padding: "10px 20px",
    backgroundColor: "#2196F3",
    color: "white",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontWeight: "500",
    fontSize: "14px",
  },
  backButton: {
    padding: "10px 20px",
    backgroundColor: "#757575",
    color: "white",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontWeight: "500",
    fontSize: "14px",
  },
  container: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "100vh",
    backgroundColor: "#f5f7fa",
  },
  loadingText: {
    fontSize: "18px",
    color: "#666",
  },
  errorText: {
    fontSize: "18px",
    color: "#d32f2f",
    marginBottom: "20px",
  },
  prescriptionContainer: {
    maxWidth: "900px",
    margin: "0 auto",
    backgroundColor: "white",
    padding: "40px",
    borderRadius: "12px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  },
  header: {
    textAlign: "center",
    marginBottom: "30px",
    paddingBottom: "20px",
    borderBottom: "2px solid #e0e0e0",
  },
  rxSymbol: {
    fontSize: "48px",
    color: "#1976D2",
    margin: "0 0 10px 0",
    fontWeight: "bold",
  },
  title: {
    fontSize: "28px",
    color: "#333",
    margin: "10px 0",
    fontWeight: "600",
  },
  date: {
    fontSize: "14px",
    color: "#999",
    margin: "5px 0 0 0",
  },
  infoGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "20px",
    marginBottom: "30px",
    "@media (max-width: 768px)": {
      gridTemplateColumns: "1fr",
    },
  },
  infoSection: {
    padding: "15px",
    backgroundColor: "#f9f9f9",
    borderRadius: "8px",
    border: "1px solid #e0e0e0",
  },
  section: {
    marginBottom: "25px",
    paddingBottom: "20px",
    borderBottom: "1px solid #e0e0e0",
  },
  sectionTitle: {
    fontSize: "16px",
    fontWeight: "600",
    color: "#1976D2",
    marginBottom: "10px",
    margin: "0 0 10px 0",
  },
  sectionContent: {
    fontSize: "14px",
    color: "#555",
    lineHeight: "1.6",
    whiteSpace: "pre-wrap",
  },
  medicinesList: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  medicineItem: {
    padding: "10px",
    backgroundColor: "#f0f8ff",
    borderLeft: "4px solid #2196F3",
    fontSize: "14px",
    color: "#333",
  },
  prescriptionImage: {
    maxWidth: "100%",
    height: "auto",
    borderRadius: "8px",
    marginTop: "10px",
  },
  statusSection: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "15px",
    padding: "20px",
    backgroundColor: "#f5f5f5",
    borderRadius: "8px",
    marginBottom: "20px",
  },
  statusItem: {
    fontSize: "14px",
    color: "#555",
  },
  statusBadge: {
    display: "inline-block",
    marginLeft: "8px",
    padding: "4px 12px",
    borderRadius: "20px",
    fontSize: "12px",
    fontWeight: "500",
    color: "white",
  },
  footer: {
    textAlign: "center",
    paddingTop: "30px",
    borderTop: "2px solid #e0e0e0",
    fontSize: "12px",
    color: "#999",
  },
};

const getStatusStyle = (status) => {
  const statusStyles = {
    ACTIVE: { backgroundColor: "#4CAF50" },
    PAYMENT_PENDING: { backgroundColor: "#FF9800" },
    PAYMENT_COMPLETED: { backgroundColor: "#2196F3" },
  };
  return statusStyles[status] || { backgroundColor: "#757575" };
};

export default PrescriptionView;